import { EventType, InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useEffect, useMemo, useReducer, useState } from 'react'

import { usersApi } from '../data/api/users.api'

import { CurrentUserContext } from './CurrentUserContext'

const REQUIRED_ROLES = [
  'Products.Read', 'Products.Write',
  'Configs.Read', 'Configs.Write',
  'Storyboards.Read', 'Storyboards.Write',
]
const ADMIN_ROLES = ['Admin.Read', 'Admin.Write']

export function CurrentUserProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const queryClient = useQueryClient()
  const [, accountChanged] = useReducer((version: number) => version + 1, 0)
  const [loggingOut, setLoggingOut] = useState(false)
  const account = instance.getActiveAccount() ?? (accounts.length > 0 ? accounts[0] : null)
  const homeAccountId = account?.homeAccountId
  const localAccountId = account?.localAccountId
  const tenantId = account?.tenantId
  const queryKey = useMemo(
    () => ['current-user', homeAccountId, localAccountId, tenantId],
    [homeAccountId, localAccountId, tenantId],
  )
  const enabled = isAuthenticated && !!account && !loggingOut && inProgress === InteractionStatus.None

  useEffect(() => {
    const callback = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGOUT_START) {
        setLoggingOut(true)
      } else if (event.eventType === EventType.LOGOUT_FAILURE || event.eventType === EventType.LOGIN_SUCCESS) {
        setLoggingOut(false)
      }

      if (
        event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
        event.eventType === EventType.ACCOUNT_REMOVED ||
        event.eventType === EventType.ACCOUNT_ADDED
      ) {
        accountChanged()
      }
    })
    return () => {
      if (callback) {
        instance.removeEventCallback(callback)
      }
    }
  }, [instance])

  useEffect(() => {
    if (!enabled) {
      void queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.removeQueries({ queryKey, exact: true })
    }

    return () => {
      void queryClient.cancelQueries({ queryKey, exact: true })
      queryClient.removeQueries({ queryKey, exact: true })
    }
  }, [enabled, queryClient, queryKey])

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => {
      if (!account) {
        throw new Error('No authenticated account is available')
      }

      return usersApi.current(account, signal)
    },
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const ready = enabled && query.isSuccess && !query.isFetching
  const roles = ready ? [...new Set(query.data.claims.filter((claim) => claim.type === 'roles').map((claim) => claim.value))] : []
  const isAdmin = ADMIN_ROLES.some((role) => roles.includes(role))

  return (
    <CurrentUserContext.Provider value={{
      isAuthenticated,
      hasAnyRole: isAdmin || REQUIRED_ROLES.some((role) => roles.includes(role)),
      isAdmin,
      roles,
      loading: isAuthenticated && (!enabled || query.isPending || query.isFetching),
      error: enabled && query.isError && !query.isFetching ? query.error : null,
      retry: () => {
        if (enabled) {
          void query.refetch()
        }
      },
      roleOverrideActive: ready && query.data.roleOverrideActive,
    }}>
      {children}
    </CurrentUserContext.Provider>
  )
}
